import { instanceOfGetDPSDetailResponse } from "@repo/api";
import {
    initializeStepperByName,
    pensionContributionChangeSelector,
    questionnaireSubmitDateSelector,
    setLastQuestionnaireSubmitDate,
    StepperIndexes,
    StepperType,
    useAmlAndIdPrerequisites,
    useAppDispatch,
    useAppSelector,
} from "@repo/logic";
import _ from "lodash";
import { FC, useEffect, useState } from "react";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { QuestionnaireQueryType } from "../PensionQuestionnaire/Questionnaire.types";
import { CustomStepper } from "../Stepper";
import { useStepperController } from "../Stepper/useStepperController";
import { BankIdStepExtraProps } from "./ChangePensionContribution.types";
import { ChangePensionContributionPages } from "./Questions";

type ChangePensionContributionStepperProps = {
    closeAction?: () => void;
} & BankIdStepExtraProps &
    QuestionnaireQueryType;

export const ChangePensionContributionStepper: FC<ChangePensionContributionStepperProps> = ({
    closeAction,
    queries,
    bankIdAuthorizationUri,
    bankIdcompleted,
    beforeBankIdAuthorizationAction,
    contractId,
    contractType,
    bankIdErrorMessage,
    scrollToEmployerSection,
}) => {
    const { useAllowedChanges, usePensionDetail } = queries;
    const dispatch = useAppDispatch();
    const { t } = useTypedTranslation();
    const change = useAppSelector(pensionContributionChangeSelector);
    const questionnaireSubmitDate = useAppSelector(questionnaireSubmitDateSelector);

    const { data: pensionDetailData, refetch: refetchPensionDetail } =
        usePensionDetail?.({ id: contractId ?? "", options: { enabled: Boolean(contractId) }, type: contractType }) ??
        {};
    const { data: allowedChangesData } =
        useAllowedChanges?.({ id: contractId ?? "", options: { enabled: Boolean(contractId) } }) ?? {};
    const isChildrenContract =
        pensionDetailData != null && instanceOfGetDPSDetailResponse(pensionDetailData)
            ? pensionDetailData.isChildrenContract
            : false;
    const { needAml, needIdentification } = useAmlAndIdPrerequisites({
        allowedChanges: allowedChangesData,
        isChildrenContract,
    });
    const [bankIdAlreadyRedirected, setBankIdAlreadyRedirected] = useState<boolean>(false);

    useEffect(() => {
        refetchPensionDetail?.();
    }, [refetchPensionDetail]);

    useEffect(() => {
        // Set last questionnaire submit date in stepper state when pension detail data is available
        if (pensionDetailData && instanceOfGetDPSDetailResponse(pensionDetailData)) {
            dispatch(
                setLastQuestionnaireSubmitDate(
                    pensionDetailData.questionnaireSubmitDate instanceof Date
                        ? pensionDetailData.questionnaireSubmitDate.toISOString()
                        : pensionDetailData.questionnaireSubmitDate,
                ),
            );
        }
    }, [dispatch, pensionDetailData]);

    // use generic controller but we still need to initialize the changePensionContribution stepper with prerequisites
    const {
        externalBusy,
        activeStepIndex,
        activeStepKey,
        totalSteps,
        activeStep,
        overlayElement,
        showConfirmModal,
        hideConfirmModal,
        handleNext,
        handleBack,
        nextButtonText,
        onCloseHandler,
    } = useStepperController<BankIdStepExtraProps>({
        name: StepperType.ChangePensionContribution,
        initializeStepper: false,
        pages: ChangePensionContributionPages,
        queries,
        extraProps: {
            bankIdAuthorizationUri,
            beforeBankIdAuthorizationAction,
            bankIdcompleted,
            bankIdErrorMessage,
            contractId,
            contractType,
            scrollToEmployerSection,
        },
        onCloseConfirmed: closeAction,
    });

    // move to next step if bankid is already completed (do it only once -> allow user to go back to bankid step manually)
    useEffect(() => {
        if (
            bankIdAlreadyRedirected === false &&
            bankIdcompleted &&
            activeStepKey === StepperIndexes.bankIdentificationPage &&
            _.isEmpty(bankIdErrorMessage)
        ) {
            setBankIdAlreadyRedirected(true);
            // advance using generic handler
            handleNext();
        }
    }, [bankIdcompleted, activeStepKey, bankIdErrorMessage, bankIdAlreadyRedirected, handleNext]);

    const isWaitingForQuestionnaireDecision =
        activeStepKey === StepperIndexes.changeContributionOverviewPage &&
        change.contributionChanged &&
        questionnaireSubmitDate.status === "notLoaded";

    // Ensure we initialize the stepper with prerequisites when known
    useEffect(() => {
        if (needAml === undefined || needIdentification === undefined) return;
        dispatch(
            initializeStepperByName({
                name: StepperType.ChangePensionContribution,
                contractId,
                useAml: needAml,
                useBankId: needIdentification,
            }),
        );
    }, [dispatch, contractId, needAml, needIdentification]);

    return (
        <CustomStepper
            externalBusy={externalBusy || isWaitingForQuestionnaireDecision}
            title={t("investmentStrategy.changePensionContribution.stepperTitle")}
            stepsCount={totalSteps}
            activeStepIndex={activeStepIndex}
            activeStepKey={activeStepKey}
            activeStep={activeStep}
            onNext={handleNext}
            onBack={handleBack}
            onClose={onCloseHandler}
            isLoading={false}
            nextButtonText={nextButtonText}
            overlay={overlayElement}
            showConfirmModal={showConfirmModal}
            hideConfirmModal={hideConfirmModal}
        />
    );
};
